/**
 * Serbian overrides for the owner screens.
 *
 * Bosnian moved from ijekavica to ekavica — *uspjela* to *uspela*, *mjesec*
 * to *mesec*, *prijedlog* to *predlog*. Where the two standards agree there
 * is no entry, which is why this file is short and should stay that way.
 *
 * Latin script, matching the rest of the application.
 *
 * The reflexes this slice actually hits, in rough order of how often:
 * *mjesec*→*mesec* (the analytics screen is built on months),
 * *posljednj*→*poslednj*, *obavješt*→*obavešt*, *izvješt*→*izvešt*,
 * *provjer*→*prover*, *ocjen*→*ocen*, *osvjež*→*osvež*, *biljež*→*belež*.
 * Alongside them one that is not a jat at all: *-šten* → *-šćen*, so
 * *iskorišteno* becomes *iskorišćeno*.
 */

import type { OwnerSlice } from '../bs/owner'

export const owner: Partial<OwnerSlice> = {
  // ---- Companies ----------------------------------------------------
  // *želite da vidite* rather than the infinitive *želite vidjeti* — the
  // da-clause is what Serbian reaches for, and it is also what removes the
  // jat from the sentence.
  'owner.companies.impersonate.description': 'Izaberite korisnika čiji ekran želite da vidite.',

  'owner.companies.form.code.hint':
    'Velika slova i brojevi. Pojavljuje se u izveštajima i nazivima datoteka.',
  'owner.companies.form.logoDark.hint':
    'Nije obavezno. Logo sa tamnim slovima se na tamnoj temi ne vidi — bez ove varijante prikazujemo ga na beloj podlozi.',
  // The example filename carries the word too, so it moves with the sentence
  // above it rather than being left as the only ijekavica on the screen.
  'owner.companies.form.logoDark.placeholder': 'https://firma.ba/logo-beli.svg',

  'owner.companies.saved.updated': 'Firma je izmenjena.',

  // ---- Impersonation ------------------------------------------------
  'owner.impersonate.notice':
    'Sesija traje jedan sat i vidljiva je korisniku. Sve što uradite beleži se u dnevniku firme pod vašim imenom.',

  // ---- Analytics ----------------------------------------------------
  'owner.analytics.period.one': 'Poslednji {count} mesec',
  'owner.analytics.period.few': 'Poslednja {count} meseca',
  'owner.analytics.period.many': 'Poslednjih {count} meseci',

  'owner.analytics.monthly.title': 'Aktivnost po mesecima',

  // ---- System health ------------------------------------------------
  'owner.health.loading': 'Provera sistema…',
  'owner.health.checkedAt': 'Provereno {when}. Osvežava se svakih 30 sekundi.',
  'owner.health.notMeasurable': 'nije merljivo',

  'owner.health.queue.description':
    'Obrada e-maila i live obaveštenja. Ako radnik stane, aplikacija i dalje radi — samo tiho prestane slati.',
  'owner.health.queue.oldest.warning':
    'Posao čeka duže od deset minuta — proverite da li kapro-queue radi.',
  'owner.health.queue.failed': 'Neuspelih',
  'owner.health.queue.lastFailure': 'Poslednji neuspeh {when}.',

  'owner.health.scheduler.description': 'Dnevni podsetnici i mesečna 5S provera.',
  'owner.health.scheduler.lastNotification': 'Poslednje obaveštenje',

  // ---- Sparkline ----------------------------------------------------
  'owner.sparkline.aria.one':
    '{count} prijava u poslednjih {days} dana, vrhunac {peak} u jednom danu.',
  'owner.sparkline.aria.few':
    '{count} prijave u poslednjih {days} dana, vrhunac {peak} u jednom danu.',
  'owner.sparkline.aria.many':
    '{count} prijava u poslednjih {days} dana, vrhunac {peak} u jednom danu.',

  // The MES integration screen. See pages/owner/CompanyMes.tsx for why the
  // secret fields explain what blank means.
  'owner.mes.secret.unchanged': 'nepromenjeno',
  'owner.mes.field.plantIds.hint': 'Vrednosti id_pogon iz MES-a, odvojene zarezom. Test ispod ih izlistava.',
  'owner.mes.webhooks.note': 'Ovde šaljemo prijave i evaluacije. MES mora imati endpoint koji ih prima i proverava potpis.',
  'owner.mes.field.webhookSecret.hint': 'Njome potpisujemo svaki zahtev, da MES može da proveri da dolazi od nas.',
  'owner.mes.deliveries.title': 'Poslednje isporuke',
  'owner.mes.failing.body': 'Neuspelih isporuka: {count}. MES ih nije primio.',

  // Ekavica: "promijeniti" is a jat the Bosnian copy carries in.
  'owner.companies.form.currency.hint':
    'U čemu ova firma računa uštede, troškove i nagrade. Postavlja se ovde pri otvaranju firme, a njihov administrator je posle može promeniti sam.',

  // ---- Team and roles -----------------------------------------------
  // *ovlašćenja*, *sme*, *menja*, *sopstvenu*, and the da-clause where
  // Bosnian takes an infinitive.
  'owner.nav.team.hint': 'Ko vodi platformu i sa kojim ovlašćenjima',
  'owner.role.support.description':
    'Vidi sve, preuzima naloge, prekida sesije i testira MES. Ne menja podešavanja firmi.',
  'owner.role.viewer.description': 'Vidi sve, ništa ne menja i ne ulazi u tuđe naloge.',
  'owner.readOnly': 'Samo za čitanje — vaša uloga ({role}) ovo ne menja. Mogu: {roles}.',
  'owner.team.subtitle': 'Ljudi koji vode platformu i šta ko sme.',
  'owner.team.invite.warning':
    'Svaki član tima vidi podatke svih firmi. Šta sme da menja zavisi od uloge — dodelite najmanju koja je dovoljna. Pozivnicu šaljemo na e-mail; lozinku postavlja sam i niko je ne vidi.',
  'owner.team.panel.description': 'Promena uloge važi odmah, i u već otvorenim sesijama.',
  'owner.team.lock.self':
    'Sopstvenu ulogu ne možete da menjate niti sebe da deaktivirate — to radi drugi član sa punim pristupom.',
  'owner.team.lock.lastFull':
    'Ovo je jedini aktivni član sa punim pristupom. Prvo nekom drugom dodelite puni pristup, inače niko ne bi mogao da vrati promenu.',

  // ---- Company list, workspace, overview ----------------------------
  'owner.companies.col.seats': 'Mesta',
  'owner.companies.col.activity': 'Poslednja aktivnost',
  'owner.suspend.reason.placeholder': 'npr. Na zahtev uprave firme, do daljeg.',
  'owner.overview.seats': 'Mesta',
  'owner.overview.online.hint': 'Koristili su je u poslednjih pet minuta.',
  'owner.overview.mes.failing.one': '{count} neuspela isporuka',
  'owner.overview.mes.failing.few': '{count} neuspele isporuke',
  'owner.overview.mes.failing.many': '{count} neuspelih isporuka',
  'owner.overview.mes.lastSync': 'Poslednja sinhronizacija {when}',
  'owner.overview.people.pendingPassword': 'Čekaju sopstvenu lozinku: {count}',
  'owner.overview.activity.description': 'Kada je šta poslednji put urađeno.',
  'owner.overview.activity.lastSignIn': 'Poslednja prijava na sistem',
  'owner.overview.activity.lastSubmission': 'Poslednja prijava',
  'owner.overview.activity.lastEvaluation': 'Poslednja 5S evaluacija',

  // ---- People, sessions, log ----------------------------------------
  'owner.people.description': 'Preuzimanje naloga i prekid sesija beleže se u dnevniku firme.',
  'owner.people.lastLogin': 'poslednji put {when}',
  'owner.impersonate.reason.placeholder':
    'npr. Ne vidi svoj zadatak od jutros — razgovor sa administratorom.',
  'owner.sessions.offline': 'Nije korišćena poslednjih pet minuta',
  'owner.sessions.col.lastUsed': 'Poslednja upotreba',
  'owner.sessions.neverUsed': 'još nekorišćena',
  'owner.activity.description': 'Izmene njihovih ljudi i sve što je odavde urađeno u vezi sa njima.',
  'owner.activity.ownerOnly.explain':
    'Obustave, izmene podešavanja, preuzimanja naloga sa razlogom, prekidi sesija i MES testovi — sve što je urađeno iz ovog panela.',

  // ---- Access -------------------------------------------------------
  'owner.access.modules.description':
    'Koji delovi aplikacije postoje za ovu firmu. Isključivanje ništa ne briše.',
  'owner.access.module.submissions.off':
    'Kad je isključen: radnici ne mogu da prijave kvar ni predlog, a rukovodioci ne vide red prijava ni zadatke.',
  'owner.access.module.submissions.hint':
    'Nestaju iz menija, kao i dugme za novu prijavu. Ništa se ne briše — uključite ga ponovo i sve je na svom mestu.',
  'owner.access.module.fiveS.hint':
    'Ocenjivači gube dugme za novi obilazak, a administrator karticu 5S obrasci. Postojeće ocene ostaju sačuvane.',
  'owner.access.security.password.description':
    'Kad je isključeno, radnici i rukovodioci se prijavljuju samo QR karticom. Administratori uvek mogu lozinkom.',
  'owner.access.security.password.hint':
    'Prva prijava aktivacionim kodom radi uvek, bez obzira na ovo — tako nalog nastaje.',
  'owner.access.security.lastMethod': 'Bar jedan način prijave mora ostati uključen.',
  'owner.access.limits.seats': 'Broj mesta',
  'owner.access.limits.seats.explain':
    'Kad su mesta popunjena, administrator firme ne može da doda ni ponovo aktivira korisnika. Noćna MES sinhronizacija se ne zaustavlja — samo prijavi prekoračenje.',
  'owner.access.confirm.title': 'Sačuvati promene za {name}?',
  'owner.access.confirm.action': 'Sačuvaj promene',
  'owner.access.dirty': 'Imate nesačuvane promene.',
  'owner.access.help':
    'Ovde se ništa ne briše: isključen modul i ograničenje samo zatvaraju vrata, a podaci ostaju i vraćaju se čim se promena poništi. Svaka izmena se beleži u dnevniku firme.',

  // ---- Integration --------------------------------------------------
  'owner.mes.locked.edit':
    'Vaša uloga ({role}) može da testira vezu i pokrene sinhronizaciju, ali ne i da menja podatke. Menjati mogu: {editors}.',
  'owner.mes.locked.all':
    'Vaša uloga ({role}) ovde samo gleda. Testirati i sinhronizovati mogu: {runners}; menjati: {editors}.',
  // *osvežava*, *promenjena* — the jat words of the owner console that
  // reached this file by inheritance.
  'owner.dashboard.subtitle': 'Sve firme zajedno. Osvežava se svakih minut.',
  'owner.activity.prop.settingsChanged': 'Promenjena podešavanja',

  // ---- Customer health ----------------------------------------------
  // *ocena*, *merenje*, *udeo*, *poslednji*, *pre*, *vredi* — the jat words
  // the score carries in — and the da-clause where Bosnian asks with *li*.
  'owner.score.description': 'Da li koriste ono što su kupili — sažeto u jednu ocenu od 0 do 100.',
  'owner.score.explain':
    'Ponderisani prosek merenja ispod. Merenje koje ne može da se izračuna ne ulazi u ocenu, a njegova težina se deli ostalima. Računa se najviše jednom u deset minuta.',
  'owner.score.loading': 'Računanje ocene…',
  'owner.score.lastActivity': 'Poslednja aktivnost {when}',
  'owner.score.ring.aria': 'Ocena zdravlja {score} od 100 — {level}',
  'owner.score.components': 'Od čega se ocena sastoji',
  'owner.score.level.healthy.description':
    'Ocena 70 ili više: koriste sistem, šire ga i odgovaraju na prijave.',
  'owner.score.level.watch.description': 'Ocena od 45 do 69: koriste sistem, ali nešto popušta.',
  'owner.score.level.atRisk.description':
    'Ocena ispod 45: korišćenje je slabo ili opada — vredi ih pozvati.',
  'owner.score.level.dormant.description': 'Niko ništa nije uradio u poslednjih 30 dana.',
  'owner.score.component.recency': 'Poslednja aktivnost',
  'owner.score.component.recency.hint':
    'Dani od poslednje aktivnosti bilo koje vrste — prijave, 5S obilaska, recenzije, završenog zadatka ili prijave na sistem. Puni bodovi do 3 dana, nula od 30 dana.',
  'owner.score.component.reach.hint':
    'Udeo aktivnih korisnika koji su u poslednjih 30 dana bilo šta uradili. Puni bodovi od 60%.',
  'owner.score.component.momentum.hint':
    'Aktivnost u poslednjih 30 dana podeljena sa aktivnošću u prethodnih 30. Puni bodovi kad nije pala, nula kad je stala.',
  'owner.score.component.responsiveness.hint':
    'Udeo prijava iz poslednjih 90 dana na koje je prvi odgovor stigao u roku od 72 sata — isto merenje kao u njihovoj Analitici. Puni bodovi od 90%.',
  'owner.score.component.fiveS.hint':
    'Udeo aktivnih radnih centara sa 5S obilaskom u poslednjih 30 dana. Puni bodovi od 80%.',
  'owner.score.component.dropped':
    'Ovo merenje ne može da se izračuna za ovog klijenta, pa ne ulazi u ocenu.',
  'owner.score.component.responsiveness.dropped.moduleOff':
    'Modul Prijave je za ovog klijenta isključen, pa odziv na prijave ne ulazi u ocenu.',
  'owner.score.component.responsiveness.dropped.noData':
    'U poslednjih 90 dana nema prijava starijih od 72 sata po kojima bi odziv mogao da se oceni, pa ne ulazi u ocenu.',
  'owner.score.component.fiveS.dropped.moduleOff':
    'Modul 5S je za ovog klijenta isključen, pa 5S pokrivenost ne ulazi u ocenu.',
  'owner.score.component.fiveS.dropped.noData':
    'Firma nema nijedan aktivan radni centar, pa 5S pokrivenost ne ulazi u ocenu.',
  'owner.score.value.days.one': 'pre {count} dan',
  'owner.score.value.days.few': 'pre {count} dana',
  'owner.score.value.days.many': 'pre {count} dana',
  'owner.score.warning.activityDrop':
    'Aktivnost je pala sa {previous} na {current} — poslednjih 30 dana prema prethodnih 30.',
  'owner.score.warning.lowReach':
    'Samo {percent} aktivnih korisnika uradilo je bilo šta u poslednjih 30 dana.',
  'owner.score.warning.fiveSCoverageLow':
    'Samo {percent} aktivnih radnih centara imalo je 5S obilazak u poslednjih 30 dana.',
  'owner.score.warning.seatsNearlyFull': 'Zauzeto je {used} od {limit} mesta.',
  'owner.score.warning.mesSyncStale': 'Poslednja MES sinhronizacija bila je pre {duration}.',
  'owner.companies.sort.aria': 'Redosled',

  // ---- Workspace: analytics -----------------------------------------
  // *mesec*, *vreme*, *rešavanje*, *predlog*, *procena*, *prosek*, *gde*,
  // *beleži*, *korišćenje*, and *sprovesti* where Bosnian says *provesti*.
  'owner.insights.window': 'Celi meseci po lokalnom vremenu fabrike, od {since}',
  'owner.insights.moduleOff.body':
    'Modul „{module}“ je isključen na kartici Pristup, pa ovde nema šta da se crta. Podaci iz vremena dok je bio uključen nisu obrisani.',
  'owner.insights.adoption.question':
    'Da li ljudi koriste naloge koje su dobili — i koliko njih svakog meseca?',
  'owner.insights.funnel.title': 'Od naloga do korišćenja',
  'owner.insights.contributors.title': 'Aktivni ljudi po mesecu',
  'owner.insights.contributors.question': 'Da li se korišćenje širi ili ga nosi nekoliko ljudi?',
  'owner.insights.contributors.hint': 'Različite osobe sa bilo kakvom aktivnošću u tom mesecu.',
  'owner.insights.process.question': 'Da li se prijave obrađuju na vreme i da li postaju uštede?',
  'owner.insights.stat.implemented': 'Sprovedena poboljšanja',
  'owner.insights.stat.onTime': 'Zadaci na vreme',
  'owner.insights.submissions.title': 'Prijave po mesecu',
  'owner.insights.submissions.hint': 'Prijave po mesecu u kojem su podnete, po vrsti.',
  'owner.insights.response.title': 'Odziv i rešavanje',
  'owner.insights.response.question': 'Koliko brzo stiže prvi odgovor, a koliko traje rešavanje?',
  'owner.insights.response.hint':
    'Medijan po mesecu prijave: od prijave do prvog odgovora i do rešenja — isto merenje kao u njihovoj Analitici.',
  'owner.insights.series.resolution': 'Rešenje',
  'owner.insights.backlog.question': 'Da li se zaostatak gomila?',
  'owner.insights.backlog.hint': 'Stanje danas, bez obzira na izabrani period.',
  'owner.insights.improvements.question': 'Da li predlozi postaju stvarne uštede?',
  'owner.insights.improvements.hint':
    'Predlozi podneti u izabranom periodu i uštede po datumu sprovođenja, u valuti klijenta.',
  'owner.insights.improvements.pipeline': 'Od predloga do sprovođenja',
  'owner.insights.improvements.implemented': 'sprovedeno',
  'owner.insights.improvements.estimated': 'Procena',
  'owner.insights.savings.empty': 'Nijedno poboljšanje nije sprovedeno u ovom periodu.',
  'owner.insights.series.savings': 'Uštede u mesecu',
  'owner.insights.tasks.question': 'Da li se zadaci završavaju na vreme?',
  'owner.insights.tasks.hint':
    'Zadaci završeni u izabranom periodu, i koliko ih je završeno do roka. Prekoračeni otvoreni zadaci su stanje danas.',
  'owner.insights.tasks.onTime': 'Od toga na vreme',
  'owner.insights.fiveS.question':
    'Da li je red na radnim mestima sve bolji i da li se obilaze svi centri?',
  'owner.insights.fiveSTrend.title': 'Prosek i pokrivenost',
  'owner.insights.fiveSTrend.question': 'Da li ocena raste i koliki deo centara se obilazi?',
  'owner.insights.fiveSTrend.hint':
    'Prosečna ocena 5S obilazaka u mesecu, i udeo aktivnih radnih centara obiđenih tog meseca.',
  'owner.insights.walks.title': 'Obilasci po mesecu',
  'owner.insights.walks.hint': 'Predati 5S obilasci po mesecu obilaska.',
  'owner.insights.series.average': 'Prosečna ocena',
  'owner.insights.usage.title': 'Korišćenje',
  'owner.insights.usage.question': 'Kada, gde i na čemu rade u aplikaciji?',
  'owner.insights.heatmap.hint':
    'Prijave, 5S obilasci, recenzije i završeni zadaci u poslednjih 90 dana, po lokalnom vremenu fabrike. Prijave na sistem nisu uključene.',
  'owner.insights.heatmap.empty': 'Nikakve aktivnosti u poslednjih 90 dana.',
  'owner.insights.heatmap.hours': 'Sat u danu, lokalno vreme fabrike',
  'owner.insights.plants.question': 'Koji pogon nosi korišćenje, a koji zaostaje?',
  'owner.insights.plants.hint':
    'Poslednjih 90 dana. Aktivni ljudi su oni koji su u tom pogonu nešto uradili.',
  'owner.insights.plants.empty': 'Nikakve aktivnosti po pogonima u poslednjih 90 dana.',
  'owner.insights.modules.question': 'Da li koriste ono što je uključeno?',
  'owner.insights.modules.hint':
    'Radnje u poslednjih 30 dana. Prijave: nove prijave i promene statusa. 5S: obilasci. KaproMES: uspešne sinhronizacije i isporuke, gde se beleže. Analitika se ne meri — otvaranje ekrana se nigde ne beleži.',
  'owner.insights.modules.notTracked': 'ne meri se',
  'owner.insights.devices.question': 'Da li rade sa telefona pored mašine ili iz kancelarije?',
  'owner.insights.devices.hint':
    'Sesije otvorene u izabranom periodu, prema podacima pregledača. Uređaji se beleže od {since}.',
  'owner.insights.devices.hint.never':
    'Sesije otvorene u izabranom periodu, prema podacima pregledača.',
  // *sreda*, *ponedeljak*, *nedelja* — and "Sre", which is why these are not
  // left to Intl for Bosnian.
  'owner.weekday.short.3': 'Sre',
  'owner.weekday.1': 'ponedeljak',
  'owner.weekday.3': 'sreda',
  'owner.weekday.7': 'nedelja',

  // ---- Portfolio ----------------------------------------------------
  'owner.portfolio.benchmarks.description':
    'Srednja vrednost među aktivnim klijentima koji su prošli uvođenje — polovina je iznad, polovina ispod.',
  'owner.portfolio.metric.health.hint':
    'Ocena zdravlja od 0 do 100 — od čega je sastavljena piše na kartici Pregled svakog klijenta.',
  'owner.portfolio.metric.reach.hint':
    'Udeo aktivnih korisnika sa bilo kakvom aktivnošću u poslednjih 30 dana.',
  'owner.portfolio.metric.submissions.hint':
    'Prijave podnete u poslednjih 30 dana; strelica je promena u odnosu na prethodnih 30.',
  'owner.portfolio.metric.response.hint':
    'Medijan vremena od prijave do prvog odgovora, za prijave iz poslednjih 90 dana.',
  'owner.portfolio.metric.fiveSAverage': '5S prosek',
  'owner.portfolio.metric.fiveSAverage.hint': 'Prosečna ocena 5S obilazaka u poslednjih 30 dana.',
  'owner.portfolio.metric.fiveSCoverage.hint':
    'Udeo aktivnih radnih centara obiđenih u poslednjih 30 dana.',
  'owner.portfolio.metric.savings.hint':
    'Ostvarene uštede poboljšanja sprovedenih ove kalendarske godine, u valuti klijenta.',
  'owner.portfolio.metric.lastActivity': 'Poslednja aktivnost',
  'owner.portfolio.platform.title': 'Cela platforma',
  'owner.portfolio.platform.description': 'Sve firme zajedno, kroz izabrani period.',

  // ---- Phase 3: security, sync runs, usage, scheduled tasks -------------
  // *beleži*, *poslednj*, *uspeš*, *neuspe*, *pre*, *vreme*, *ovde*, *gde*,
  // *mesto*, *proveri*, *promeni*, *sledeći* — the reflexes this part hits —
  // plus *sa* before a consonant and the da-clause where Bosnian asks with
  // *li*.
  'owner.status.unknown.hint': 'Ova vrednost je novija od ove verzije aplikacije.',

  'owner.security.recordingSince': 'Pokušaji prijave beleže se od {date}',
  'owner.security.recordingSince.partial':
    'Pokušaji prijave beleže se tek od {date} — dani pre toga nisu prazni, nego nezabeleženi.',
  'owner.security.period.one': 'Poslednji {count} dan',
  'owner.security.period.few': 'Poslednja {count} dana',
  'owner.security.period.many': 'Poslednjih {count} dana',
  'owner.security.notRecorded.title': 'Prijave se još ne beleže',
  'owner.security.notRecorded.description':
    'Beleženje pokušaja prijave počinje sa ovom verzijom. Prvi pokušaj posle toga — uspešan ili ne — pojaviće se ovde. Do tada nema šta da se prikaže: nula bi značila „niko nije pogrešio“, a istina je „niko nije brojao“.',

  'owner.security.stat.succeeded': 'Uspešne prijave',
  'owner.security.stat.succeeded.hint':
    'Prijave koje su završile ulaskom u aplikaciju u izabranom periodu, lozinkom ili karticom. Ispod: koliko je različitih ljudi ušlo, od svih aktivnih naloga.',
  'owner.security.stat.failed': 'Neuspele',
  'owner.security.stat.failed.hint':
    'Pokušaji sa pogrešnim podatkom: nepoznata šifra ili kartica, pogrešna lozinka ili kod, neaktivan nalog. Ispod: odbijeni (tačni podaci, zatvorena vrata) i blokirani (previše pokušaja).',
  'owner.security.stat.rate': 'Stopa neuspeha',
  'owner.security.stat.rate.hint':
    'Neuspeli pokušaji od neuspelih i uspešnih zajedno. Do 15% je uobičajeno jer ljudi greše u kucanju; od 30% je ili pogrešno podeljena lozinka ili neko ko nije njihov.',
  'owner.security.stat.twoFactor.hint':
    'Aktivni administratori sa uključenom dvofaktorskom zaštitom. Kad je obavezna, administrator bez nje ne može u administraciju. Svi nalozi: {covered} od {accounts}.',
  'owner.security.stat.badges.hint':
    'Aktivni nalozi kojima je izdata QR kartica za prijavu. Ispod: uspešne prijave lozinkom i karticom u izabranom periodu.',
  'owner.security.stat.lastSuccess': 'Poslednja uspešna',
  'owner.security.stat.lastSuccess.hint':
    'Poslednja uspešna prijava bilo koga iz ove firme, bez obzira na izabrani period.',

  'owner.security.chart.question': 'Da li ljudi ulaze bez problema, i kad je bilo neuspeha?',
  'owner.security.chart.hint':
    'Svaki pokušaj prijave po lokalnom danu i ishodu. Zahtev za drugi korak se ne crta: njegov ishod je sledeći zapis. Dani pre početka beleženja nisu nacrtani.',
  'owner.security.chart.aria':
    'Pokušaji prijave po danu, {days} dana: uspešnih {succeeded}, neuspelih {failed}, odbijenih {refused}, blokiranih {throttled}.',
  'owner.security.series.succeeded': 'Uspešne',
  'owner.security.series.failed': 'Neuspele',

  'owner.security.failures.one': '{count} neuspeo pokušaj',
  'owner.security.failures.few': '{count} neuspela pokušaja',
  'owner.security.failures.many': '{count} neuspelih pokušaja',
  'owner.security.showAttempts.ip': 'Prikaži pokušaje prijave sa adrese {ip}',

  'owner.security.repeated.title': 'Ponovljeni neuspesi',
  'owner.security.repeated.question': 'Ko ne može da uđe — ili ko pokušava tuđim nalogom?',
  'owner.security.repeated.hint':
    'Ljudi sa tri ili više neuspelih pokušaja u izabranom periodu, najviše deset, oni sa najviše neuspeha prvi. Ime otvara osobu na kartici Korisnici.',
  'owner.security.repeated.none': 'Niko nije imao tri ili više neuspelih pokušaja.',
  'owner.security.repeated.lastFailure': 'poslednji neuspeh {when}',
  'owner.security.repeated.lastSuccess': 'poslednja uspešna prijava {when}',
  'owner.security.repeated.noSuccess': 'bez ijedne uspešne prijave',

  'owner.security.afterFailures.title': 'Uspeh posle niza neuspeha',
  'owner.security.afterFailures.question': 'Da li je neko pogodio lozinku?',
  'owner.security.afterFailures.hint':
    'Uspešna prijava kojoj je u satu pre nje prethodilo pet ili više neuspelih pokušaja na isti nalog. Može biti i sama osoba koja se setila iz šestog puta — zato je treba pitati.',
  'owner.security.afterFailures.alert':
    'Ovako izgleda pogođena lozinka. Proverite sa tom osobom da li je to bila ona; ako nije, prekinite joj sesije i neka promeni lozinku.',
  'owner.security.afterFailures.none': 'Nijedna uspešna prijava nije usledila posle niza neuspelih.',
  'owner.security.afterFailures.count': 'Neuspelih pokušaja u satu pre toga: {count}',
  'owner.security.afterFailures.at': 'uspešna prijava {when}',

  'owner.security.ips.title': 'Adrese sa najviše neuspeha',
  'owner.security.ips.hint':
    'IP adrese sa tri ili više neuspelih pokušaja na naloge ove firme u izabranom periodu, najviše deset. Mnogo naloga sa jedne adrese znači da neko ide redom po spisku.',
  'owner.security.ips.none': 'Nijedna adresa nije imala tri ili više neuspelih pokušaja.',
  'owner.security.ips.lastAt': 'poslednji {when}',

  'owner.security.log.hint':
    'Svaki pokušaj prijave, najnoviji prvi. Ono što je upisano — lozinka, kod, kartica — nigde se ne čuva, pa ga nema ni ovde.',
  'owner.security.log.empty.description': 'Za ovu firmu još nije zabeležen nijedan pokušaj prijave.',
  'owner.security.col.when': 'Vreme',

  'owner.security.outcome.succeeded': 'Uspešna',
  'owner.security.outcome.succeeded.hint':
    'Osoba je ušla: podaci su bili tačni, a gde se traži drugi korak, prošla je i njega.',
  'owner.security.outcome.challenged.hint':
    'Lozinka ili kartica je tačna i zatražen je kod dvofaktorske zaštite. Ishod drugog koraka beleži se kao poseban zapis.',
  'owner.security.outcome.failed': 'Neuspela',
  'owner.security.outcome.throttled.hint':
    'Previše pokušaja u kratkom vremenu, pa server odbija bez provere podataka. Zaštita od pogađanja lozinke; prestaje sama posle kratkog vremena.',
  'owner.security.reason.unknownBadge.hint':
    'Skenirana kartica ne pripada nijednom nalogu — poništena je, zamenjena novom ili je tuđa.',
  'owner.security.reason.wrongRecoveryCode.hint': 'Rezervni kod nije tačan ili je već iskorišćen.',
  'owner.security.reason.challengeExpired.hint':
    'Drugi korak nije završen na vreme, ili se nalog u međuvremenu promenio — deaktiviran je ili mu je isključena dvofaktorska zaštita.',

  'owner.mes.runs.hint':
    'Beleži se od ove verzije. Aktivacioni kodovi koje je sinhronizacija izdala ovde se samo broje — nikad se ne čuvaju.',
  'owner.mes.runs.none':
    'Još nijedna zabeležena sinhronizacija. Beleže se od ove verzije, pa i integracija koja radi mesecima ovde počinje praznom; prvo noćno ili ručno pokretanje pojaviće se ovde.',
  'owner.mes.runs.lastSuccess': 'Poslednja uspešna',
  'owner.mes.runs.lastFailure': 'Poslednja neuspela',
  'owner.mes.runs.consecutive': 'Neuspeha zaredom',
  'owner.mes.runs.consecutive.hint':
    'Neuspela ili prekinuta pokretanja od poslednjeg uspešnog. Jedno vredi pogledati; tri znače da se ljudi i mašine ne ažuriraju danima.',
  'owner.mes.runs.successRate': 'Uspešnost, 30 dana',
  'owner.mes.runs.successRate.hint': 'Udeo završenih pokretanja u poslednjih 30 dana koja su uspela.',
  'owner.mes.runs.failingInARow.one': '{count} pokretanje zaredom nije uspelo.',
  'owner.mes.runs.failingInARow.few': '{count} pokretanja zaredom nisu uspela.',
  'owner.mes.runs.failingInARow.many': '{count} pokretanja zaredom nije uspelo.',
  'owner.mes.runs.recent': 'Poslednjih pet',
  'owner.mes.runs.status.succeeded': 'Uspela',
  'owner.mes.runs.status.failed': 'Neuspela',
  'owner.mes.runs.status.interrupted.hint':
    'Pokrenuta pre više od sat vremena i nikad završena — proces je ugašen usred rada, npr. restartom servera.',

  'owner.usage.projection.seats.one': 'Mesta će biti popunjena za ~{count} dan.',
  'owner.usage.projection.seats.few': 'Mesta će biti popunjena za ~{count} dana.',
  'owner.usage.projection.seats.many': 'Mesta će biti popunjena za ~{count} dana.',
  'owner.usage.projection.seats.full': 'Sva mesta su popunjena — novi korisnik ne može da se doda.',
  'owner.usage.projection.short.full': 'već nema mesta',
  'owner.usage.projection.seats.distant': 'Pri ovom rastu mesta neće biti popunjena ni za deset godina.',
  'owner.system.task.remindOverdue': 'Jutarnji podsetnik na kašnjenja',
  'owner.system.task.remindEvaluations': 'Podsetnik na mesečne 5S ocene',
  'owner.system.task.sendMesReports': 'Ocene i 5S pregled u KaproMES',
  'owner.system.task.snapshotUsage': 'Dnevni zapis prostora i mesta',
  'owner.system.task.evaluateAlerts': 'Provera upozorenja',
  'owner.system.task.pruneHistory': 'Brisanje stare istorije',
  'owner.usage.history.since': 'Beleži se od {date} — za liniju trebaju bar dva dana.',
  'owner.usage.spark.seats': 'Zauzeta mesta kroz {days} dana: od {from} do {to}.',

  'owner.system.recordingSince': 'Beleži se od {date}',
  'owner.system.tasks.question': 'Da li raspored radi — i da li je svaki zadatak došao na red kad je trebalo?',
  'owner.system.tasks.hint':
    'Svaki zadatak iz rasporeda aplikacije, redom kojim je zakazan: poslednje pokretanje, koliko je trajalo i kada je sledeće.',
  'owner.system.tasks.notRecorded':
    'Pokretanja se još ne beleže — prvo pokretanje rasporeda posle ove verzije pojaviće se ovde.',
  'owner.system.tasks.summary.failing': 'neuspelo: {count}',
  'owner.system.tasks.summary.ok': 'sve na vreme',
  'owner.system.tasks.col.status.hint':
    'Uspeo, Neuspeo, Radi, Preskočen (prethodno pokretanje još traje ili ga je preuzeo drugi server) ili Prekinut (ugašen usred rada). „Kasni“ ima prednost: zadatak koji je prestao da se pokreće i dalje pokazuje zeleni ishod poslednjeg pokretanja.',
  'owner.system.tasks.col.lastRun': 'Poslednje pokretanje',
  'owner.system.tasks.col.next': 'Sledeće',
  'owner.system.tasks.col.next.hint':
    'Prema izrazu rasporeda, u njegovoj vremenskoj zoni. Tačno vreme je ispod pokazivača.',
  'owner.system.tasks.col.failures': 'Neuspeha, 7 dana',
  'owner.system.tasks.col.failures.hint':
    'Neuspela pokretanja u poslednjih sedam dana, od ukupnog broja pokretanja.',
  'owner.system.tasks.late.hint':
    'Trebalo je da krene pre više od 15 minuta, a nijedno pokretanje nije zabeleženo. Najčešće znači da schedule:work ne radi.',
  'owner.system.tasks.neverRun.hint': 'Od početka beleženja ovaj zadatak još nijednom nije bio na redu.',
  'owner.system.tasks.status.succeeded': 'Uspeo',
  'owner.system.tasks.status.succeeded.hint': 'Završen sa izlaznim kodom 0.',
  'owner.system.tasks.status.failed': 'Neuspeo',
  'owner.system.tasks.status.interrupted.hint':
    'Pokrenut pre više od sat vremena i nikad završen — proces je ugašen usred rada.',

  'owner.system.mes.question': 'Da li noćna sinhronizacija stiže do svakog klijenta koji je ima?',
  'owner.system.mes.panel': 'Klijenti sa podešenim MES-om',
  'owner.system.mes.hint':
    'Svi klijenti sa podešenom adresom MES-a, po nazivu. Naziv otvara njihovu karticu Integracija sa celom istorijom.',
  'owner.system.mes.noRun': 'još bez zabeleženog pokretanja',
  'owner.system.mes.col.lastRun': 'Poslednje pokretanje',
  'owner.system.mes.col.deliveries.hint': 'Događaji poslati u njihov MES: neuspeli i oni koji još čekaju.',
  'owner.system.mes.failed': 'neuspelo {count}',

  'owner.system.storage.growth.hint':
    'Zbir dnevnog rasta svih klijenata — prava linija kroz njihovih poslednjih 30 dana. Potrebno je najmanje sedam dnevnih zapisa.',
  'owner.system.storage.diskFull.hint':
    'Slobodan prostor podeljen ukupnim dnevnim rastom. Okvirno: prava linija ne zna za novog klijenta.',
  'owner.system.storage.diskFull.none': 'bez rasta ili disk nije merljiv',

  'owner.system.signIns.question': 'Ko pokušava da uđe, u koju firmu — i da li neko pokušava da uđe u naš tim?',
  'owner.system.signIns.notRecorded':
    'Beleženje pokušaja prijave počinje sa ovom verzijom. Prvi pokušaj posle toga, bilo kog klijenta ili vlasnika, pojaviće se ovde.',
  'owner.system.signIns.owners.hint':
    'Nalozi našeg tima otvaraju svakog klijenta, pa i jedan neuspeh vredi pogledati, a pet ili blokada je hitno.',
  'owner.system.signIns.owners.none': 'Nijedan neuspeli pokušaj na naloge vlasnika u izabranom periodu.',
  'owner.system.signIns.owners.failed':
    'Neuspelih pokušaja na naloge vlasnika: {failed} · blokirano: {throttled}',
  'owner.system.signIns.owners.lastFailure': 'Poslednji neuspeh',
  'owner.system.signIns.owners.lastSuccess': 'Poslednja uspešna',
  'owner.system.signIns.unknown.hint':
    'Pokušaji koji nisu odgovarali nijednom nalogu, pa ne pripadaju nijednoj firmi. Ovde se prvo vidi neko ko pogađa.',
  'owner.system.signIns.byCompany.hint':
    'Svaki klijent po nazivu, za izabrani period. Naziv otvara njihovu karticu Sigurnost.',
  'owner.system.signIns.col.repeated': 'Ponovljeni neuspesi',

  // ---- Phase 4: the overview and alerts ---------------------------------
  // The jat reflex as everywhere, and one word that is not a jat at all:
  // Serbian puts an alert off with *odložiti*, where Bosnian *odgađa*.
  'owner.dashboard.system.queue.failing': 'neuspelih u 24 h: {count}',
  'owner.dashboard.system.mes.failing': 'ne uspeva: {failing} od {configured}',

  'owner.dashboard.pulse.description': 'Danas i ovaj mesec, prema istim danima prošlog meseca. Samo klijenti.',
  'owner.dashboard.pulse.hint':
    '„Isti dani prošlog meseca“ znači od prvog do današnjeg dana u mesecu, da se pola meseca ne poredi sa celim. Linija je poslednjih 30 dana, po lokalnom vremenu.',
  'owner.dashboard.pulse.signIns.hint': 'Uspešne prijave u aplikaciju, lozinkom ili karticom.',
  'owner.dashboard.pulse.activePeople.hint':
    'Različiti ljudi klijenata koji su u periodu nešto uradili ili se uspešno prijavili.',
  'owner.dashboard.pulse.thisMonth': 'ovaj mesec',
  'owner.dashboard.pulse.month': 'ovaj mesec: {count}',
  'owner.dashboard.pulse.change.up': '▲ {percent} više nego istih dana prošlog meseca ({previous})',
  'owner.dashboard.pulse.change.down': '▼ {percent} manje nego istih dana prošlog meseca ({previous})',
  'owner.dashboard.pulse.change.same': 'isto kao istih dana prošlog meseca ({previous})',
  'owner.dashboard.pulse.change.fromZero': 'istih dana prošlog meseca: 0',
  'owner.dashboard.pulse.spark': '{label} po danu, poslednjih {days} dana: ukupno {total}, najviše {peak} u jednom danu.',

  'owner.dashboard.activity.description': 'Poslednjih 15 radnji vlasnika, kod svih klijenata.',
  'owner.dashboard.activity.empty': 'Još nijedne zabeležene radnje vlasnika.',

  'owner.alerts.description': 'Sve što provere nađu, na jednom mestu — kod klijenata i na platformi.',
  'owner.alerts.hint':
    'Provere se pokreću svakih deset minuta. Upozorenje se samo zatvara kad uzrok nestane; kartica Rešeno pamti šta je bilo otvoreno i ko se time bavio.',
  'owner.alerts.evaluatedAt': 'Provereno {when}',
  'owner.alerts.notEvaluated': 'Provere se još nisu pokrenule',
  'owner.alerts.evaluate': 'Proveri sada',
  'owner.alerts.evaluated': 'Provera je završena.',

  'owner.alerts.state.attention.hint': 'Otvoreno, a niko ga još nije preuzeo ni odložio.',
  'owner.alerts.state.snoozed': 'Odloženo',
  'owner.alerts.state.snoozed.hint': 'Odloženo do datuma, a ranije se vraća ako se stanje pogorša.',
  'owner.alerts.state.resolved': 'Rešeno',
  'owner.alerts.state.resolved.hint': 'Uzrok je nestao i provera je upozorenje sama zatvorila.',

  'owner.alerts.lastSeen': 'Poslednja provera koja ga je našla: {when}',
  'owner.alerts.snoozedBy': 'Odloženo do {until} — {name}',
  'owner.alerts.snoozedAnon': 'Odloženo do {until}',
  'owner.alerts.resolvedAt': 'rešeno {when} · trajalo {duration}',
  'owner.alerts.resolvedAtShort': 'rešeno {when}',

  'owner.alerts.action.snooze': 'Odloži',
  'owner.alerts.action.unsnooze': 'Prekini odlaganje',
  'owner.alerts.done.snoozed': 'Odloženo do {until}.',
  'owner.alerts.done.unsnoozed': 'Odlaganje je prekinuto.',
  'owner.alerts.done.muted': 'Utišano. Upozorenje se zatvara najkasnije pri sledećoj proveri.',
  'owner.alerts.done.unmuted':
    'Utišavanje je ukinuto. Ako uzrok i dalje postoji, upozorenje se vraća pri sledećoj proveri.',

  'owner.alerts.snooze.title': 'Odložiti do datuma',
  'owner.alerts.snooze.confirm': 'Odloži',

  'owner.alerts.mute.body':
    'Utišano upozorenje se zatvara i ne otvara se ponovo dok se utišavanje ne ukine. Provera i dalje radi — samo se o njoj više ne javlja, ni ovde ni e-mailom.',
  'owner.alerts.mute.scope.platform': 'Ovo upozorenje se odnosi na celu platformu, pa se utišava svuda.',
  'owner.alerts.mute.reason.placeholder': 'npr. Klijent namerno ne koristi 5S do proleća.',

  'owner.alerts.mutes.description':
    'Vrste upozorenja koje se ne prikazuju, za jednog klijenta ili za sve. Kad ukinete utišavanje, upozorenje se vraća pri sledećoj proveri ako uzrok i dalje postoji.',
  'owner.alerts.mutes.empty': 'Ništa nije utišano — prikazuje se sve što provere nađu.',

  'owner.alerts.empty.attention': 'Poslednja provera, {when}, nije našla ništa što čeka da ga neko preuzme.',
  'owner.alerts.empty.never':
    'Provere se još nisu pokrenule. Pokreću se svakih deset minuta — ako ovo stoji duže, pogledajte raspored na stranici Sistem.',
  'owner.alerts.empty.snoozed': 'Ništa nije odloženo.',
  'owner.alerts.empty.resolved': 'Još nijedno upozorenje nije rešeno.',

  'owner.alerts.company.description': 'Otvorena upozorenja ovog klijenta, iz svih provera — ne samo iz ocene zdravlja.',

  // *korišćeni*, as *iskorišćeno* elsewhere in this file.
  'owner.alerts.type.health.neverActivated': 'Nalozi koji nikad nisu korišćeni',
  'owner.alerts.type.health.seatsNearlyFull': 'Mesta skoro popunjena',
  'owner.alerts.type.security.repeatedFailures': 'Ponovljene neuspele prijave',
  'owner.alerts.type.security.ownerSignInFailures': 'Neuspele prijave na naloge vlasnika',
  'owner.alerts.type.security.unknownCodeAttempts': 'Pokušaji sa nepoznatim šiframa',
  'owner.alerts.type.mes.syncFailing': 'MES sinhronizacija ne uspeva',
  'owner.alerts.type.limits.seatsFillSoon': 'Mesta se popunjavaju',
  'owner.alerts.type.scheduler.failing': 'Zakazani zadatak ne uspeva',
  'owner.alerts.type.queue.failedJobs': 'Neuspeli poslovi u redu čekanja',

  'owner.alerts.fact.signInAfterFailures': 'Nalog {code}: {failures}, pa uspešna prijava {when}, sa adrese {ip}.',
  'owner.alerts.fact.signInAfterFailures.noIp': 'Nalog {code}: {failures}, pa uspešna prijava {when}.',
  'owner.alerts.fact.repeatedFailures': 'Nalog {code}: {failures} u poslednja 24 sata, poslednji {when}.',
  'owner.alerts.fact.ownerSignInFailures':
    'Nalozi vlasnika u poslednja 24 sata — neuspelih pokušaja: {failed}, blokiranih: {throttled}. Poslednji {when}.',
  'owner.alerts.fact.scanningAddress':
    'Adresa {ip} u poslednja 24 sata — neuspelih pokušaja: {failures}, različitih naloga: {accounts}. Poslednji {when}.',
  'owner.alerts.fact.unknownCodeAttempts':
    'Neuspelih pokušaja sa nepoznatom šifrom ili karticom u poslednja 24 sata: {failed}. Poslednji {when}.',
  'owner.alerts.fact.mesSyncFailing': '{runs} Poslednja uspešna {when}.',
  'owner.alerts.fact.mesSyncFailing.never': '{runs} Nijedna još nije uspela.',
  'owner.alerts.fact.mesDeliveriesFailing': 'Neuspelih isporuka u poslednja 24 sata: {failed}. Poslednja {when}.',
  'owner.alerts.fact.seatsUsed': 'Zauzeto {used} od {limit} mesta.',
  'owner.alerts.fact.schedulerLate': 'Zadatak „{task}“ trebalo je da krene {when}, a nije pokrenut.',
  'owner.alerts.fact.schedulerFailing': 'Zadatak „{task}“ nije uspeo {when}.',
  'owner.alerts.fact.queueFailedJobs': 'Neuspelih poslova u poslednja 24 sata: {count}. Poslednji {when}.',

  'owner.alertPrefs.immediate.description':
    'E-mail čim se otvori hitno upozorenje koje niko nije preuzeo ni odložio — jedan po proveri, koliko god ih bilo.',
  'owner.alertPrefs.digest.description':
    'Svako jutro u 7:30: šta je otvoreno, šta je preuzeto ili odloženo, i šta se otvorilo i zatvorilo za 24 sata. Ne stiže kad nema šta da se kaže.',
}
